import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Button, Input, Card, Alert, Loader, ConfirmacionDialog } from '@rfacil/shared/ui';
import { useForm, useNotificacionStore } from '@rfacil/shared/hooks';
import { validationUtils } from '@rfacil/shared/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faArrowLeft, faShieldHalved, faKey } from '@fortawesome/free-solid-svg-icons';
import { useRecuperarPassword, useValidarClave } from '../hooks/useEnvioCorreo';

/**
 * Pantalla para solicitar recuperación de contraseña por correo electrónico.
 */
export const OlvideContrasena: React.FC = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotificacionStore();
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [codigoCorreo, setCodigoCorreo] = useState('');
  const [codigoError, setCodigoError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const mutation = useRecuperarPassword();
  const validarClaveMutation = useValidarClave();

  const { values, errors, setValue, validateForm, isFormValid } = useForm(
    { correo: '' },
    {
      correo: (value: string) =>
        !value.trim()
          ? 'El correo es requerido'
          : !validationUtils.isValidEmail(value.trim())
            ? 'Ingresa un correo electrónico válido'
            : null,
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) return;

    setConfirmOpen(true);
  };

  const handleCloseConfirm = () => {
    if (mutation.isPending) return;
    setConfirmOpen(false);
  };

  const handleConfirmSend = async () => {
    const correo = values.correo.trim();
    setError(null);
    setConfirmOpen(false);
    try {
      const result = await mutation.mutateAsync(correo);
      if (result.Estatus) {
        setSent(true);
        setCodigoCorreo('');
      } else {
        showNotification(
          result.Descripcion?.trim() || 'No se pudo enviar el código. Intenta de nuevo.',
          'error'
        );
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'No se pudo enviar la solicitud. Intenta de nuevo.';
      setError(errorMessage);
    }
  };

  const handleValidarCodigo = async (e: React.FormEvent) => {
    e.preventDefault();
    setCodigoError(null);
    const clave = codigoCorreo.trim();
    if (!clave) {
      setCodigoError('Ingresa el código que recibiste por correo.');
      return;
    }
    try {
      const result = await validarClaveMutation.mutateAsync({
        Id: 0,
        IdUsuario: 0,
        Clave: clave,
        FechaVigencia: new Date().toISOString(),
        Correo: values.correo.trim(),
      });
      if (result.Estatus) {
        navigate('/restablecer-contrasena', {
          replace: true,
          state: {
            correo: values.correo.trim(),
            claveRecuperacion: clave,
          },
        });
      } else {
        showNotification(
          result.Descripcion?.trim() || 'El código no es válido. Verifica e intenta de nuevo.',
          'error'
        );
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'No se pudo validar el código. Intenta de nuevo.';
      setCodigoError(errorMessage);
    }
  };

  return (
    <>
      {mutation.isPending && (
        <Loader message="Enviando solicitud..." subtitle="Por favor espera unos segundos" />
      )}
      {validarClaveMutation.isPending && (
        <Loader message="Validando código..." subtitle="Por favor espera unos segundos" />
      )}
      <div className="min-h-screen flex flex-col bg-background-primary">
        <div className="flex-1 flex min-h-0">
          <div className="hidden lg:flex lg:w-1/2 items-center justify-center bg-gradient-to-br p-4">
            <div className="w-full ">
              <img
                src="/LOGOSRFACIL_CONSLOGAN.svg"
                alt="Rfacil Materialidad"
                className="w-full h-auto object-contain max-w-md mx-auto"
              />
            </div>
          </div>

          <div className="w-full lg:w-1/2 flex items-center justify-center p-5">
            <Card className="w-full max-w-md" padding="lg">
              <div className="text-center mb-8">
                <div className="flex justify-center mb-4">
                  <div className="w-40 h-20 rounded-lg flex items-center justify-center ">
                    <img src="/ICONO IMAGOTIPO.png" alt="Logo" className="w-full h-full object-contain" />
                  </div>
                </div>

                <h1 className="text-gray-800 mb-2 text-3xl font-bold">Recuperar contraseña</h1>
                <p className="text-gray-600 text-sm">
                  Escribe el correo asociado a tu cuenta. Te enviaremos instrucciones para restablecerla.
                </p>
              </div>

              {sent ? (
                <div className="space-y-6">
                  <Alert variant="success">
                    <p className="font-medium mb-1">El código fue enviado correctamente.</p>
                    <p className="text-sm leading-relaxed">
                      Revisa tu bandeja de entrada (y spam). El código tiene una validez de{' '}
                      <span className="font-semibold">15 minutos</span>.
                    </p>
                  </Alert>
                  <form onSubmit={(ev) => void handleValidarCodigo(ev)} className="space-y-4">
                    <div>
                      <Input
                        label="Código recibido por correo"
                        type="text"
                        value={codigoCorreo}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setCodigoCorreo(e.target.value.slice(0, 8));
                          if (codigoError) setCodigoError(null);
                        }}
                        error={codigoError ?? undefined}
                        leftIcon={<FontAwesomeIcon icon={faKey} className="text-gray-400" />}
                        placeholder="Máximo 8 caracteres"
                        required
                      />
                    </div>
                    <Button
                      type="submit"
                      fullWidth
                      size="lg"
                      disabled={validarClaveMutation.isPending}
                      className="bg-gradient-to-r from-host-500 to-host-600 hover:from-host-600 hover:to-host-700 text-text-secondary font-medium shadow-md"
                    >
                      Validar código
                    </Button>
                  </form>
                  <Button
                    type="button"
                    fullWidth
                    size="lg"
                    variant="ghost"
                    disabled={validarClaveMutation.isPending}
                    onClick={() => navigate('/login')}
                    className="border border-host-500 text-host-600 hover:bg-host-50"
                  >
                    <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                    Volver al inicio de sesión
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="mb-6">
                    <Input
                      label="Correo electrónico"
                      type="email"
                      value={values.correo}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setValue('correo', e.target.value)
                      }
                      error={errors.correo}
                      required
                      leftIcon={<FontAwesomeIcon icon={faEnvelope} className="text-gray-400" />}
                      placeholder="tu.correo@ejemplo.com"
                    />
                  </div>

                  {error && (
                    <Alert variant="error" className="mb-5">
                      {error}
                    </Alert>
                  )}

                  <Button
                    type="submit"
                    fullWidth
                    size="lg"
                    disabled={!isFormValid() || mutation.isPending}
                    className="bg-gradient-to-r from-host-500 to-host-600 hover:from-host-600 hover:to-host-700 text-text-secondary font-medium shadow-md"
                  >
                    Continuar
                  </Button>

                  <p className="text-center mt-4">
                    <Link
                      to="/login"
                      className="text-sm text-host-600 hover:underline font-medium inline-flex items-center gap-2"
                    >
                      <FontAwesomeIcon icon={faArrowLeft} className="text-xs" />
                      Volver al inicio de sesión
                    </Link>
                  </p>
                </form>
              )}
            </Card>
          </div>
        </div>

        <footer
          className="shrink-0 border-t border-host-200/60 bg-gradient-to-b from-host-50/90 to-background-primary py-5 px-4 sm:px-8"
          role="contentinfo"
          aria-label="Aviso de privacidad"
        >
          <div className="max-w-2xl mx-auto flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-5">
            <div className="flex justify-center sm:justify-start shrink-0" aria-hidden>
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-host-100 text-host-600 shadow-sm ring-1 ring-host-200/50">
                <FontAwesomeIcon icon={faShieldHalved} className="text-lg" />
              </span>
            </div>
            <div className="text-center sm:text-left space-y-1.5">
              <p className="text-sm font-semibold text-gray-800">Privacidad y protección de datos</p>
              <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                La información que nos proporciones se utiliza solo para gestión y comunicación relacionada con el
                servicio.{' '}
                <a
                  href="https://rfacil.com.mx/aviso-de-privacidad/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-host-600 font-medium underline decoration-host-300 underline-offset-2 hover:text-host-700 hover:decoration-host-500 transition-colors"
                >
                  Consulta nuestro Aviso de privacidad
                </a>
                .
              </p>
            </div>
          </div>
        </footer>
      </div>

      <ConfirmacionDialog
        open={confirmOpen}
        onClose={handleCloseConfirm}
        onConfirm={() => {
          void handleConfirmSend();
        }}
        zIndex={12000}
        title="Confirmar envío"
        subtitle="Revisa el correo antes de enviar las instrucciones"
        message={
          <>
            ¿Enviar las instrucciones de recuperación de contraseña a{' '}
            <span className="font-semibold text-slate-900 break-all">{values.correo.trim()}</span>?
          </>
        }
        confirmText="Sí, enviar"
        cancelText="Cancelar"
      />
    </>
  );
};

export default OlvideContrasena;
